import React from "react";
import styled from "styled-components";
import { fonts } from "../../common/mixins";
import Overlay from "../../common/Overlay";
import {
  OptionsPopup,
  Price,
  ProductActionsWrapper,
  ProductDescription,
  ProductMedia,
  ProductPrice,
} from "./ProductCard";
import { StoreProduct } from "../../../stores/useReservationStore";
import {
  GradientText,
  ScrollEndSentinel,
  useElementRef,
  useIsVisibleIn,
} from "./scrollFade";

const ProductDetailsColumns = styled.div`
  display: grid;
  grid-template-columns: 180px auto;
  gap: 16px;
  height: 100%;
  overflow-y: auto;
`;

const ProductDetailsImageColumn = styled.div`
  width: 180px;
`;

const ProductDetailsInfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  position: relative;

  ${ProductActionsWrapper} {
    padding-right: 16px;
  }

  ${OptionsPopup} {
    right: 16px;
    max-height: 100%;
    margin: 0;
  }
`;

const ProductDetailsInfo = styled.div`
  overflow-y: auto;
  min-height: 0;
  scrollbar-width: thin;
  scrollbar-color: darkgray #fff;
  padding-right: 8px;
`;

const ProductDetailsInfoWrapper = styled.div`
  flex: 1 1 auto;
  display: grid;
  overflow-y: hidden;
  padding-bottom: 16px;
  position: relative;
`;

const ProductDetailsTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ProductDetailsName = styled.div`
  ${fonts.title()};
  line-height: normal;
`;

const ProductDetailsPrice = styled.div`
  ${Price} {
    font-weight: 400;
  }
`;

interface ProductDescriptionModalProps {
  open: boolean;
  onClose: () => void;
  id: string;
  product: StoreProduct | null;
  /** Add button / options popup, rendered by Products (it owns that state). */
  actions: React.ReactNode;
}

// Product details opened from a card's description link: image, scrollable
// description and the add actions.
export function ProductDescriptionModal({
  open,
  onClose,
  id,
  product,
  actions,
}: ProductDescriptionModalProps) {
  const [scrollNode, scrollRef] = useElementRef<HTMLDivElement>();
  const [endNode, scrollEndRef] = useElementRef<HTMLDivElement>();
  const isScrollEndVisible = useIsVisibleIn(scrollNode, endNode);

  return (
    <Overlay
      handleClose={onClose}
      open={open}
      titleAlignTop
      titlePaddingBottom={16}
      width={560}
      maxHeight={598}
      contentSx={{
        overflowY: "hidden",
        display: "grid",
        paddingRight: "8px !important",
      }}
      id={id}
      titleText={
        product ? (
          <ProductDetailsTitle>
            <ProductDetailsName>{product.name}</ProductDetailsName>
            <ProductDetailsPrice>
              <ProductPrice options={product.productOptions} />
            </ProductDetailsPrice>
          </ProductDetailsTitle>
        ) : (
          ""
        )
      }
    >
      {product ? (
        <ProductDetailsColumns>
          <ProductDetailsImageColumn>
            <ProductMedia product={product} bordered />
          </ProductDetailsImageColumn>
          <ProductDetailsInfoColumn>
            {product.description ? (
              <ProductDetailsInfoWrapper>
                <ProductDetailsInfo ref={scrollRef}>
                  <ProductDescription id="product-description-modal">
                    {product.description}
                  </ProductDescription>
                  <ScrollEndSentinel ref={scrollEndRef} />
                </ProductDetailsInfo>
                <GradientText $hidden={isScrollEndVisible} />
              </ProductDetailsInfoWrapper>
            ) : null}
            {actions}
          </ProductDetailsInfoColumn>
        </ProductDetailsColumns>
      ) : null}
    </Overlay>
  );
}
